/**
 * Feature flags for the sync-engine rollout (plans/06_07_2026/).
 *
 * `sync_engine_v1` is per-workspace: a workspace only routes through the
 * SQLite store once it has been explicitly enrolled (and, from Phase 1, has a
 * verified migration). Dormant in Phase 0 — nothing enrolls workspaces yet.
 *
 * `extensions_marketplace` (Phase 6) is a plain boolean: first-party
 * extensions now ship bundled into the app, so the marketplace surface
 * (Browse Marketplace UI + background update sync) is hidden by default. The
 * code stays in the repo compiling — it's the future third-party
 * distribution path, to be re-exposed only once the extension API contract
 * is frozen (after the core sync refactor lands).
 *
 * Persistence is plain localStorage rather than appPreferencesStore so the
 * flags can be read synchronously before any store hydration (bootstrap.js
 * runs at import time), and flipping them never touches the preferences
 * schema.
 */

const STORAGE_KEY = 'apiboo-sync-engine-v1-workspaces';
const KILL_SWITCH_KEY = 'apiboo-sync-engine-v1-disabled';
const MARKETPLACE_KEY = 'apiboo-extensions-marketplace-enabled';

function readEnrolled() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function writeEnrolled(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* storage unavailable — flag stays session-local */
  }
}

/** Global kill switch wins over any enrollment (rollback lever). */
export function syncEngineKilled() {
  try {
    return localStorage.getItem(KILL_SWITCH_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * @param {{ id?: string, localId?: string } | string | null} workspace
 * @returns {boolean} whether this workspace routes through the SQLite store
 */
export function syncEngineEnabled(workspace) {
  if (!workspace || syncEngineKilled()) return false;
  const id = typeof workspace === 'string' ? workspace : workspace.localId || workspace.id;
  if (!id) return false;
  return readEnrolled().has(String(id));
}

export function enrollWorkspaceInSyncEngine(workspaceId) {
  const set = readEnrolled();
  set.add(String(workspaceId));
  writeEnrolled(set);
}

export function unenrollWorkspaceFromSyncEngine(workspaceId) {
  const set = readEnrolled();
  set.delete(String(workspaceId));
  writeEnrolled(set);
}

/**
 * Whether the extensions marketplace surface (Browse Marketplace UI +
 * background update sync) is visible. Default OFF — bundled first-party
 * extensions are the shipping delivery path (Phase 6). Dev override:
 * `localStorage.setItem('apiboo-extensions-marketplace-enabled', 'true')`
 * (or setMarketplaceEnabled below) and reload.
 */
export function marketplaceEnabled() {
  try {
    return localStorage.getItem(MARKETPLACE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setMarketplaceEnabled(on) {
  try {
    if (on) localStorage.setItem(MARKETPLACE_KEY, 'true');
    else localStorage.removeItem(MARKETPLACE_KEY);
  } catch {
    /* storage unavailable — flag stays at its default (off) */
  }
}

const DOCS_STACKED_KEY = 'apiboo-documents-stacked-sections';

/**
 * Documents redesign (card 1.2, 13.10.2026): the sidebar shows ONE tree at a
 * time behind the scope switcher. The old stacked layout (Personal, This
 * device and every team as collapsible sections, one under the other) stays
 * reachable for one release in case the owner wants both:
 * `localStorage.setItem('apiboo-documents-stacked-sections', 'true')` + reload.
 */
export function documentsStackedSections() {
  try {
    return localStorage.getItem(DOCS_STACKED_KEY) === 'true';
  } catch {
    return false;
  }
}
